import { z } from 'zod';
import { CurrencyCode, PositiveCents } from './primitives.js';
/**
 * The payments facet's vocabulary (spec §3.7, §13) — types and arithmetic only.
 *
 * This file exists in `packages/domain` for the same reason `capabilitiesFor`
 * does: **three surfaces need the same answer and none of them may be the
 * authority for the others.** `provider-api` prices a booking deposit when the
 * agent asks for a payment link; `core-api` prices the same deposit when the
 * counter texts one, and again when PayPal's capture webhook arrives; the CLI
 * validates the policy an operator types. A second copy of "20 % of the price,
 * floored at five dollars, never more than is owed" would eventually disagree
 * with the first, and the disagreement would be a guest charged the wrong
 * amount.
 *
 * Nothing here does I/O and nothing here knows what PayPal is. The PayPal port
 * lives in `apps/core-api` (the only service that talks to the rail); what is
 * *shared* is what a payment link is, what a tenant's policy says, and what the
 * amount works out to.
 */
/* ------------------------------------------------------------------ */
/* The stored lifecycle, and the four states the wire admits            */
/* ------------------------------------------------------------------ */
/**
 * What the `payment_links` row goes through.
 *
 * Five states rather than the wire's four, because the two extra ones are real
 * and we watch them: `approved` is the state PayPal reports when the buyer has
 * clicked through but the capture has not landed (the spike watched a sandbox
 * checkout sit there while the buyer's spinner span), and `failed` is a capture
 * that came back declined. Collapsing either into "pending" would make an
 * operator's screen say "waiting" about a payment that is never coming.
 */
export const PaymentLinkState = z.enum(['requested', 'approved', 'completed', 'expired', 'failed']);
/**
 * What the partner sees — spec §3.7 states exactly these four
 * (`pending · paid · expired · cancelled`), and the wire keeps them.
 *
 * The agent's only question is "may I still tell the guest this link works, and
 * has the money arrived?", which four states answer. Widening the wire to the
 * stored five would publish a vocabulary the agreement did not agree to and
 * would oblige every consumer to handle a state that means nothing to them.
 */
export const PaymentLinkStatus = z.enum(['pending', 'paid', 'expired', 'cancelled']);
/**
 * The stored state, projected onto the wire — and **expiry derived, not
 * stored**, exactly as a certificate's is (`repos/money.ts`) and a hold's is.
 *
 * A link is expired when its clock has run out and nothing terminal has
 * happened to it. Deriving it means a sweeper that stalls cannot cause a dead
 * link to be reported live — the failure mode a column-plus-sweeper design has
 * and never announces.
 *
 * `failed → cancelled` is the one mapping worth reading twice. From the agent's
 * side both mean the same actionable thing: this link will not be paid, stop
 * waiting on it and offer the guest another way. `pending` would be a lie that
 * costs the guest a wait.
 */
export function wirePaymentStatus(state, expiresAt, now) {
    if (state === 'completed')
        return 'paid';
    if (state === 'failed')
        return 'cancelled';
    if (state === 'expired')
        return 'expired';
    return expiresAt.getTime() <= now.getTime() ? 'expired' : 'pending';
}
/**
 * What a link is *for*. Spec §3.7's table names four; three are modelled and
 * only `appointment` is wired today (the ordering and certificate facets are
 * designed and unbuilt, and `stay` waits on §4.7's lodging model).
 *
 * The enum carries the unwired kinds deliberately: the column type is the one
 * place a referent that nothing can price would otherwise be storable, and a
 * migration per facet for a value the design already names is a migration for
 * nothing. The *route* refuses what it cannot price; the type says what the
 * design is.
 */
export const PaymentReferentKind = z.enum(['appointment', 'order', 'certificate']);
/**
 * **The referent, and never an amount.** This is B7 stated where it matters
 * most (spec §3.7): "an LLM that can mint a payment link for an arbitrary
 * amount is a price-manipulation machine." The partner names *what* is being
 * paid for; the server computes *how much*, from rows it already owns.
 *
 * There is no `amount_cents` on this object and there never may be one.
 */
export const PaymentReferent = z.object({
    kind: PaymentReferentKind,
    id: z.string().min(1).max(128),
});
/* ------------------------------------------------------------------ */
/* The floor a real fee schedule puts under a deposit                  */
/* ------------------------------------------------------------------ */
/**
 * The smallest link this system will mint, in cents.
 *
 * Not a style choice — a measurement. The PayPal spike captured $1.00 on the
 * sandbox and read the settlement back: `paypal_fee` **0.52**, net 0.48
 * (docs/paypal-spike.md, "Fees on a $1.00 capture"). The fixed component
 * dominates small amounts, so a one-dollar deposit hands PayPal half of it and
 * a fifty-cent one costs the tenant money to collect. A dollar is the absolute
 * refusal; a sensible tenant sets `min_cents` far above it, and the default
 * policy below does.
 */
export const PAYMENT_LINK_MIN_CENTS = 100;
/**
 * A tenant's deposit rule.
 *
 * `percent` and `fixed` rather than a single number because both are real: a
 * spa asks for a fixed $50 to hold a couples suite, a tour operator asks for
 * 25 % of a departure. `min_cents` is the fee floor made per-tenant, and it is
 * a floor rather than a refusal — a 20 % policy on a $15 add-on should collect
 * the tenant's minimum, not decline to hold the booking.
 */
export const DepositPolicy = z.object({
    /** When false, no booking of this tenant's ever pre-mints a deposit link. */
    required: z.boolean().default(false),
    mode: z.enum(['percent', 'fixed']).default('percent'),
    /** Used when `mode = percent`. Whole percent; 20 means 20 %. */
    percent: z.number().int().min(1).max(100).default(20),
    /** Used when `mode = fixed`. */
    fixed_cents: PositiveCents.default(5_000),
    /** The floor. Never below `PAYMENT_LINK_MIN_CENTS`; see the constant. */
    min_cents: PositiveCents.default(500),
});
/**
 * The tenant's payments configuration, as it lives in `tenant_config.config`.
 *
 * **The credential is a reference, not a secret** (docs/paypal-spike.md's
 * decision record): `client_id` is the public half PayPal itself puts in
 * browser URLs, and `secret_ref` is a Secret Manager resource name — the same
 * custody discipline `webhook_endpoints.secret_ref` and `TWILIO_AUTH_TOKEN_REF`
 * already run. The secret itself is never a column, never an env var in
 * production, and never passed to a CLI.
 *
 * `webhook_id` is PayPal's id for the webhook subscription registered on *that
 * tenant's* app. It is required by `/v1/notifications/verify-webhook-signature`
 * — a receiver cannot verify a signature without knowing which subscription the
 * event claims to belong to — so a tenant with no `webhook_id` can mint links
 * and cannot settle them. That is stated here rather than discovered at 2 a.m.:
 * `paymentsUsable` demands it.
 *
 * **The interim model is first-party per-tenant credentials.** Every call is
 * signed with the tenant's own app — "us as them", not "us on behalf of them" —
 * so no partner grant gates it. When the JSWaves partner application clears,
 * migrating a tenant is a swap of the values behind this document and nothing
 * on the wire changes. That is the seam; `assertion` is where it will land.
 */
export const TenantPaymentsConfig = z.object({
    /** One rail today. Named so that a second one is a value, not a schema change. */
    provider: z.literal('paypal').default('paypal'),
    environment: z.enum(['sandbox', 'live']).default('sandbox'),
    client_id: z.string().min(1).max(256),
    /** A Secret Manager resource name. The secret is never here. */
    secret_ref: z.string().min(1).max(512),
    /** PayPal's webhook subscription id on this tenant's app. */
    webhook_id: z.string().max(128).default(''),
    /** How long a minted link stays good. A texted link should outlive the call. */
    link_ttl_hours: z.number().int().min(1).max(720).default(72),
    deposit: DepositPolicy.default(DepositPolicy.parse({})),
});
/**
 * Read the payments block out of a config document that may predate it.
 *
 * The same shape as `defaultLocaleOf`: `tenant_config.config` is *cast* rather
 * than parsed on the hot path, so every reader needs one answer to "this tenant
 * has no payments block at all", and one function is where it lives. `null`
 * means unconfigured, which is what the capability projection turns into
 * `payments: "none"`.
 */
export function paymentsConfigOf(config) {
    const raw = config?.payments;
    if (raw === undefined || raw === null)
        return null;
    const parsed = TenantPaymentsConfig.safeParse(raw);
    return parsed.success ? parsed.data : null;
}
/**
 * Can this tenant actually take money?
 *
 * Both halves are needed and the second is the one that gets forgotten: an app
 * that can create an order but no registered webhook subscription produces
 * links a guest can pay and a book that never learns about it — money collected
 * and no `prepaid_at`, which is the §7.4 invariant failing silently. So
 * "configured" means *settleable*, not merely *mintable*.
 */
export function paymentsUsable(payments) {
    return (payments !== null &&
        payments.client_id !== '' &&
        payments.secret_ref !== '' &&
        payments.webhook_id !== '');
}
/**
 * What a deposit on this booking comes to — the **policy**, floored, capped by
 * what is actually owed.
 *
 * Three rules, in this order, and the order is the argument:
 *
 *  1. **The policy decides.** It is what the tenant agreed to charge and the
 *     only number the guest was ever told. The alternative — "the remaining
 *     balance" — is not a deposit at all, it is prepayment in full, and a spa
 *     that asks 20 % to hold a room would start charging 100 % the day someone
 *     changed a default.
 *  2. **The floor lifts it.** `min_cents`, never below `PAYMENT_LINK_MIN_CENTS`,
 *     because PayPal's fixed fee eats a small capture whole (the spike's $0.52
 *     on $1.00). A policy that computes $3 collects the tenant's minimum.
 *  3. **What is owed caps it.** Asking for more than the bill is an overcharge
 *     the counter has to refund, and the counter is not in the conversation.
 *     If the cap lands under the floor, no link is minted at all: a two-dollar
 *     remainder is not worth a payment rail's fee, and "pay at the counter" is
 *     the honest answer.
 *
 * Rounding is `Math.round` on the percent branch — half a cent up, once, at the
 * only place a percentage becomes money.
 */
export function depositRequiredCents(policy, basis, floorCents = PAYMENT_LINK_MIN_CENTS) {
    if (!policy.required)
        return { requiredCents: 0, reason: 'not_required' };
    const owed = Math.max(0, basis.priceCents - (basis.discountCents ?? 0) - (basis.prepaidCents ?? 0));
    if (owed <= 0)
        return { requiredCents: 0, reason: 'nothing_owed' };
    const fromPolicy = policy.mode === 'fixed'
        ? policy.fixed_cents
        : Math.round((basis.priceCents * policy.percent) / 100);
    const floored = Math.max(fromPolicy, policy.min_cents, floorCents);
    const capped = Math.min(floored, owed);
    if (capped < floorCents)
        return { requiredCents: 0, reason: 'below_floor' };
    return { requiredCents: capped, reason: 'policy' };
}
/* ------------------------------------------------------------------ */
/* The record, as every surface reads it                               */
/* ------------------------------------------------------------------ */
/**
 * A payment link on the wire (spec §3.7's response shape).
 *
 * `url` is what the guest was handed — recorded on the row rather than
 * recomputed, because the guest has that exact string in a text message and a
 * later config change must not make our copy disagree with theirs.
 */
export const PaymentLink = z.object({
    payment_link_id: z.string().min(1).max(128),
    url: z.string().url(),
    amount_cents: PositiveCents,
    currency: CurrencyCode,
    status: PaymentLinkStatus,
    referent: PaymentReferent,
    expires_at: z.string(),
});
//# sourceMappingURL=payments.js.map